

// this turns an html element into an expandable/collapsible accordion
// it makes no decisions about style.

// this is necessary instead of a component or some other solution because the web
// +react isn't really well prepared to implement an accordion that performs well.
// an accordion needs to animate its 'height' property, but you can't do that when
// the heights of your stuff are instrinsic/not known ahead of time (and when you do
// the animation time scales with the height of the stuff, which is obviously bad),
// because 'height: auto' isn't animatable. 

// In chrome/edge you have the css `interpolate-size` which actually would resolve this, 
// but you'd need something else for firefox/safari.

// Some people get around this by animating 'transform: scaleY(...)' instead, but this causes
// your contents to be skewed and squished, and even then only works where the collapsed state
// is height === 0. You also end up having your elements in the DOM still take up the space they
// otherwise would, as if they had 'position: relative;'.

// so it seems the only robust option is to manually animate the measured heights using javascript,
// which isn't too crazy (it's below). But this also has huge pitfalls if you implement it naively
// using react. It's tempting to make a state variable 'collapsed' which you call 'setCollapsed' on,
// but that turns this potentially smoothly-animating accordion into a frame stutter machine, because
// the superfluous 're-renders' that react does will reset the state of your elements on screen, breaking
// the animation. If you instead trigger the animation off of a useEffect, effectively 'waiting' for the
// re-render to be done and therefore skipping the frame-stutters, you do in fact skip the frame stutters,
// but now you are waiting a few hundred milliseconds for the re-render to complete before the animation 
// kicks off! which makes the experience sluggish anyway. We actually do expose a collapsed/setCollapsed API,
// but it makes sure to only dispatch re-renders when the animation is not mid-flight, so you won't get stutters.


// the provided 'accordionRef' must refer to an html element with the following css (we can't apply it ourselves
// because the ref we get passed is initially null, and the only way to get it to be non-null is to trigger a
// re-render, which maybe we don't want to do for performance reasons):

//    transition: height 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); // any transition for 'height' is ok.
//    height: auto; // leaving the height property unset also seems to generally work.
//    overflow: hidden;

// the ref element itself should have a 'ref' which will be passed to us like:
//    <div 
//      ref={myAccordionRef}
//      className={styles.myAccordionStyle} // <-- this class should have the 'transition/height: auto/overflow' properties on it
//    > 
//      ... 
//    </div>

// `useAccordion()` will add a `data-collapsed={boolean}` attribute to the ref'd element, using the specified default.
// It will override the element height upon initial render if the default is collapsed.

// the specifics of more or less everything in this hook (in particular when which function calls
// happen relative to each other) are very sensitive to changes, @NOTE(nick) let me know if you find
// bugs, because changing stuff is likely to break things in other areas

import { elementIsInViewport } from "@/utils/viewportUtils";
import { useEffect, useState } from "react";

// there is an options argument to 'addEventListener' that allows an event to fire a single time only,
// { once: true }, but I couldn't get that to work
const oneTimeEventListener = (element: HTMLElement, eventType: string, callback: Function) => {
  const handler = () => {
    callback();
    element.removeEventListener(eventType, handler);
  }
  element.addEventListener(eventType, handler);
}

const collapse = (accordionRef: React.MutableRefObject<HTMLElement>, onCollapseChange: (collapsed: boolean) => void, collapsedHeight: string) => {
  onCollapseChange(true);

  const element = accordionRef.current;
  // get the height of the element's inner content, regardless of its actual size
  const sectionHeight = element.scrollHeight;

  // temporarily disable all css transitions
  const elementTransition = element.style.transition;
  element.style.transition = '';

  // on the next frame (as soon as the previous style change has taken effect),
  // explicitly set the element's height to its current pixel height, so we 
  // aren't transitioning out of 'auto'

  // do this first, so you don't get a (couple) frames where the content is visible while the accordion is mid-collapse
  element.style.overflow = "hidden";

  requestAnimationFrame(() => {
    element.style.height = sectionHeight + 'px';
    element.style.transition = elementTransition;

    // on the next frame (as soon as the previous style change has taken effect),
    // have the element transition to the collapsed height
    requestAnimationFrame(() => {
      element.style.height = collapsedHeight;
    });

    oneTimeEventListener(element, "transitionend", () => {
      element.setAttribute("data-collapsed", "true");

      requestAnimationFrame(() => {
        // scroll to the accordion. for very large accordions that collapse
        // leaving you further down the page than you intended, this can be good.
        // @TODO perhaps should be optional?
        if (!elementIsInViewport(element)) element.scrollIntoView();
      });
    });
  });
}

const expand = (accordionRef: React.MutableRefObject<HTMLElement>, setCollapsed: (collapsed: boolean) => void) => {
  setCollapsed(false);

  const element = accordionRef.current;

  // get the height of the element's inner content, regardless of its actual size
  var sectionHeight = element.scrollHeight;

  // have the element transition to the height of its inner content
  element.style.height = sectionHeight + 'px';

  // when the next css transition finishes (which should be the one we just triggered)
  oneTimeEventListener(element, "transitionend", () => {
    // remove "height" from the element's inline styles, so it can return to its initial value
    element.style.height = null;

    // @TODO(nick) parameterize this? if the accordion is fully-expanded, we want the overflow to work normally.
    // this is important if you have, say, a 'Select' component inside the accordion.
    element.style.overflow = "";

    element.setAttribute("data-collapsed", "false");
  });
}

export default function useAccordion(
  accordionRef: React.MutableRefObject<HTMLElement>,
  defaultCollapsed: boolean = true,
  collapsedHeight: string = "2.25rem", // use '0px' or something for a completely collapsed accordion
): { collapsed: boolean, setCollapsed: (set: boolean) => void, toggleCollapsed: () => void} {
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);

  const toggleCollapsed = () => {
    if (collapsed) {
      expand(accordionRef, setCollapsed);
    } else {
      collapse(accordionRef, setCollapsed, collapsedHeight);
    }
  }

  const _setCollapsed = c => {
    if (c === collapsed) return;
    toggleCollapsed();
  }

  useEffect(() => {
    if (accordionRef.current && !accordionRef.current.hasAttribute("data-collapsed")) {
      accordionRef.current.setAttribute("data-collapsed", `${defaultCollapsed}`);
      if (defaultCollapsed) {
        accordionRef.current.style.height = collapsedHeight;
      }
    }
  }, [accordionRef.current]);
  
  return { collapsed, setCollapsed: _setCollapsed, toggleCollapsed };
};
